// src/components/Billing.js
import { BrandButton, ErrorContainedButton, TextCancelButton } from '../button';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  TableFooter,
  TablePagination,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Avatar,
} from '@mui/material';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { getStripe } from '../../utils/stripeClient';
import axios from 'axios';
import { isAdminOrManagerRole, isAdminRole } from '../../utils/userRoles';
import { useOutletContext, useLocation } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import { getNames, getCode } from 'country-list';
import EmptyState from '../EmptyState';
import { PLAN_LIMITS, resolvePlanLimit } from '../../config/billingPlans';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoIcon from '@mui/icons-material/Info';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PriceWithTax from '../PriceWithTax';
import usePricingPlans from '../../hooks/usePricingPlans';
import SubscriptionSavings from '../SubscriptionSavings';
import SmsBillingTab from './SmsBillingTab';
import SmsCreditsUsageBar from '../SmsCreditsUsageBar/SmsCreditsUsageBar';
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  PageBlockHeader,
  Tabs,
  Tab,
  Card,
  CardContent,
  Table,
  Dialog,
  DialogTitleText,
  DialogBody,
  DialogFooter,
  Button,
  Chip,
  Input,
  MenuItem,
  TableActionsMenu,
  TableActionsMenuButton,
  acmeTabsSx,
} from '../ui';
import {
  acme,
  acmeBillingSectionContentSx,
  acmePageSectionCardSx,
  acmePlanCardSx,
  acmePlanBadgeSx,
  acmeInfoCalloutSx,
  acmeInfoCardActiveSx,
  acmeDisabledInputSx,
  acmeTextFieldSx,
} from '../../theme/acmeTheme';

const countriesList = getNames();

function Billing() {
  const { user, updateUser } = useOutletContext();
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(location.state?.activeTab || 0);
  const [currentPlan, setCurrentPlan] = useState({ name: 'Basic', limit: PLAN_LIMITS.Basic });
  const [usedEmails, setUsedEmails] = useState(0);
  const [billingHistory, setBillingHistory] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);
  
  // *** ИСПОЛЬЗУЕМ НОВЫЙ ХУК ДЛЯ УПРАВЛЕНИЯ ПЛАНАМИ ***
  const { plans: availablePlans, loading: plansLoading, error: plansError } = usePricingPlans(user, totalSpent);
  const [invoiceActionMenu, setInvoiceActionMenu] = useState({ anchorEl: null, record: null });
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();
  const [billingAllCities, setBillingAllCities] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogField, setDialogField] = useState(null);
  const [dialogValue, setDialogValue] = useState({
    company: '',
    address: '',
    country: '',
    city: '',
    postalCode: '',
    hasVat: 'no',
    vatNumber: '',
    email: ''
  });
  // *** NEW: Subscription-related state ***
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isSubscription, setIsSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // *** NEW: Payment choice dialog state ***
  const [paymentChoiceOpen, setPaymentChoiceOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // *** NEW: Cancellation confirmation dialog ***
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const userStatus = user?.status;  // ????????: 'Active' ??? 'Block'
  const planExpired = currentPlan.limit !== Infinity && usedEmails >= currentPlan.limit;

  // *** UPDATED: Calculate billing dates based on subscription or saved planPurchasedDate ***
  // Используем сохраненную дату покупки плана вместо последней записи в billing history
  let purchaseDate;
  if (isSubscription && subscriptionInfo) {
    // Для подписок используем начало текущего периода
    purchaseDate = new Date(subscriptionInfo.current_period_start);
  } else if (user?.planPurchasedDate) {
    // Используем сохраненную дату покупки плана
    purchaseDate = new Date(user.planPurchasedDate);
  } else {
    // Fallback: используем дату создания аккаунта
    purchaseDate = new Date(user?.createdAt || Date.now());
  }
  // For subscriptions, use subscription period, for one-time payments use manual calculation
  let nextPaymentDate, nextPaymentDateString;
  if (isSubscription && subscriptionInfo) {
    nextPaymentDate = subscriptionInfo.current_period_end;
    nextPaymentDateString = nextPaymentDate ? nextPaymentDate.toLocaleDateString() : 'N/A';
  } else if (purchaseDate) {
    // Находим следующий месяц с тем же днем, который еще не наступил
    const today = new Date();
    const dayOfMonth = purchaseDate.getDate();
    nextPaymentDate = new Date(today.getFullYear(), today.getMonth(), dayOfMonth);
    // Если этот день уже прошел в текущем месяце, берем следующий месяц
    if (nextPaymentDate <= today) {
      nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
    }
    nextPaymentDateString = nextPaymentDate ? nextPaymentDate.toLocaleDateString() : 'N/A';
  } else {
    nextPaymentDate = null;
    nextPaymentDateString = 'N/A';
  }

  // Calculate renewal date for Basic plan - based on registration date
  const today = new Date();
  const userCreatedAt = new Date(user?.createdAt || Date.now());
  const basicPlanRenewalDate = new Date(today.getFullYear(), today.getMonth(), userCreatedAt.getDate());
  
  // If today's date has passed the renewal day this month, move to next month
  if (today.getDate() > userCreatedAt.getDate()) {
    basicPlanRenewalDate.setMonth(basicPlanRenewalDate.getMonth() + 1);
  }
  
  const basicPlanRenewalDateString = basicPlanRenewalDate.toLocaleDateString();

  // Check if payment is due soon (within 7 days)
  const daysDifference = nextPaymentDate ? Math.ceil((nextPaymentDate - today) / (1000 * 60 * 60 * 24)) : null;
  const isPaymentDueSoon = daysDifference !== null && daysDifference <= 7 && daysDifference > 0;
  const isPaymentOverdue = daysDifference !== null && daysDifference < 0 && currentPlan.name !== 'Basic';
  const isBasicPlanExhausted = currentPlan.name === 'Basic' && planExpired;

  // Helper function to check if user is restricted
  const isUserRestricted = () => {
    return user?.status === 'Moderation' || user?.status === 'Block';
  };

  // Определяем процент использования перед его использованием в условиях
  const usagePercent = currentPlan.limit && currentPlan.limit !== Infinity
    ? Math.round((usedEmails / currentPlan.limit) * 100)
    : 0;

  let accountStatusVariant = 'success';
  let statusText, StatusIcon;

  if (userStatus === 'Block') {
    accountStatusVariant = 'error';
    statusText = 'User Blocked';
    StatusIcon = BlockIcon;
  } else if (userStatus === 'Moderation') {
    accountStatusVariant = 'warning';
    statusText = 'Account under moderation';
    StatusIcon = WarningIcon;
  } else if ((planExpired && currentPlan.name !== 'Basic') || (isPaymentOverdue && currentPlan.name !== 'Basic')) {
    accountStatusVariant = 'error';
    statusText = planExpired ? 'Plan expired' : 'Payment overdue';
    StatusIcon = WarningIcon;
  } else if (isBasicPlanExhausted) {
    accountStatusVariant = 'warning';
    statusText = 'Basic plan limit reached';
    StatusIcon = WarningIcon;
  } else if (isPaymentDueSoon && currentPlan.name !== 'Basic') {
    accountStatusVariant = 'warning';
    statusText = 'Payment due soon';
    StatusIcon = WarningIcon;
  } else if (usagePercent >= 90 && usagePercent < 100) {
    accountStatusVariant = 'warning';
    statusText = 'Approaching limit';
    StatusIcon = WarningIcon;
  } else {
    accountStatusVariant = 'success';
    statusText = 'Plan active';
    StatusIcon = CheckCircleIcon;
  }

  const accountStatusIconColor =
    accountStatusVariant === 'success'
      ? acme.colors.brand
      : accountStatusVariant === 'error'
        ? acme.colors.destructive
        : acme.colors.warning;

  // Обработчик переключения табов
  const handleChangeTab = (event, newValue) => {
    setTabIndex(newValue);
  };

  const loadCurrentPlan = useCallback(async () => {
    try {
      const response = await axios.get('/api/billing/current-plan', { withCredentials: true });
      const data = response.data;
      setCurrentPlan({
        name: data.planName,
        limit: resolvePlanLimit(data.planName, data.planLimit),
      });
      setUsedEmails(data.usedEmails);
      setIsSubscription(data.isSubscription || false);
      setSubscriptionStatus(data.subscriptionStatus);
      setSubscriptionInfo(data.subscriptionInfo);
      
      // Сохраняем planPurchasedDate в user для использования в вычислениях
      if (data.planPurchasedDate && typeof updateUser === 'function') {
        updateUser({ planPurchasedDate: data.planPurchasedDate });
      }
      
      console.log('Plan data loaded:', {
        planName: data.planName,
        isSubscription: data.isSubscription,
        subscriptionStatus: data.subscriptionStatus,
        subscriptionInfo: data.subscriptionInfo,
        planPurchasedDate: data.planPurchasedDate
      });
    } catch (error) {
      console.error('Failed to load current plan:', error);
      setErrorMessage('Failed to load plan information');
    }
  }, [setErrorMessage, updateUser]);

  const loadBillingHistory = useCallback(async () => {
    try {
      const response = await axios.get('/api/billing/history', { withCredentials: true });
      setBillingHistory(response.data);
      
      // Calculate total spent
      const total = response.data.reduce((sum, item) => {
        const amount = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
        return sum + amount;
      }, 0);
      setTotalSpent(total);
    } catch (error) {
      console.error('Failed to load billing history:', error);
    }
  }, []);

  // *** UPDATED: Load current plan and subscription info ***
  useEffect(() => {
    loadCurrentPlan();
    loadBillingHistory();
    
    // *** ОБРАБОТКА selectedPlan из Pricing страницы ***
    if (location.state?.selectedPlan) {
      const p = location.state.selectedPlan;
      setSelectedPlan({
        ...p,
        limit:
          p?.name && PLAN_LIMITS[p.name] !== undefined
            ? PLAN_LIMITS[p.name]
            : p.limit,
      });
      setPaymentChoiceOpen(true);
      // Очищаем state чтобы диалог не открывался повторно
      window.history.replaceState(null, '');
    }
  }, [location.state, loadCurrentPlan, loadBillingHistory]);

  useEffect(() => {
    fetch('/api/cities')
      .then(response => response.json())
      .then(data => setBillingAllCities(data))
      .catch(err => console.error('Failed to load city data:', err));
  }, []);

  // Return from Stripe SMS top-up checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('sms_topup');
    if (!status) return;
    setTabIndex(2);
    if (status === 'success') {
      setSuccessMessage('Payment received. Your SMS balance will update shortly.');
    } else if (status === 'cancel') {
      setErrorMessage('SMS top-up was canceled.');
    }
    window.history.replaceState(null, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // *** NEW: Handle upgrade button click - show payment choice dialog ***
  const handleUpgradeClick = (plan) => {
    // Basic plan is free - no upgrade needed
    if (plan.name === 'Basic') {
      return;
    }

    // Check if trying to downgrade
    const currentPlanIndex = availablePlans.findIndex(p => p.name === currentPlan.name);
    const selectedPlanIndex = availablePlans.findIndex(p => p.name === plan.name);
    
    if (currentPlanIndex > selectedPlanIndex) {
      setErrorMessage(`You currently have the ${currentPlan.name} plan. You can switch to the ${plan.name} plan after your current plan expires on ${nextPaymentDateString}.`);
      return;
    }

    // Check billing information
    if (!user?.billingAddress || !user?.billingCity || !user?.billingPostalCode || !user?.billingCountry) {
      setErrorMessage('Please complete your billing information (address, city, postal code, and country) before proceeding with payment.');
      setTabIndex(3);
      return;
    }

    // Лимит только из конфига — не доверяем устаревшему state с Pricing/кэша
    setSelectedPlan({
      ...plan,
      limit:
        plan?.name && PLAN_LIMITS[plan.name] !== undefined
          ? PLAN_LIMITS[plan.name]
          : plan.limit,
      oneTimePrice: plan.oneTimePrice || 0,
      subscriptionPrice: plan.subscriptionPrice || 0,
    });
    setPaymentChoiceOpen(true);
  };

  // *** NEW: Handle one-time payment ***
  const handleOneTimePayment = async () => {
    try {
      console.log('Initiating one-time payment for plan:', selectedPlan.name);

      const response = await axios.post('/api/billing/create-checkout-session', {
        planName: selectedPlan.name,
        price: selectedPlan.oneTimePrice,
        planLimit: selectedPlan.limit,
      });
      
      const sessionId = response.data.sessionId;
      console.log('One-time payment session created:', sessionId);
      
      const stripe = await getStripe();
      if (!stripe) {
        setErrorMessage('Stripe is not configured. Set REACT_APP_STRIPE_PUBLIC_KEY in frontend/.env');
        setPaymentChoiceOpen(false);
        return;
      }
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Stripe checkout error:', error);
        setErrorMessage('Failed to initiate payment. Please try again later.');
      }
      setPaymentChoiceOpen(false);
    } catch (error) {
      console.error('One-time payment error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to initiate payment. Please try again later.';
      setErrorMessage(errorMessage);
      setPaymentChoiceOpen(false);
    }
  };

  // *** NEW: Handle subscription payment ***
  const handleSubscriptionPayment = async () => {
    try {
      console.log('Initiating subscription for plan:', selectedPlan.name);
      
      const response = await axios.post('/api/subscriptions/create-checkout', {
        planName: selectedPlan.name,
      });
      
      const sessionId = response.data.sessionId;
      console.log('Subscription session created:', sessionId);
      
      const stripe = await getStripe();
      if (!stripe) {
        setErrorMessage('Stripe is not configured. Set REACT_APP_STRIPE_PUBLIC_KEY in frontend/.env');
        setPaymentChoiceOpen(false);
        return;
      }
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Stripe subscription error:', error);
        setErrorMessage('Failed to initiate subscription. Please try again later.');
      }
      setPaymentChoiceOpen(false);
    } catch (error) {
      console.error('Subscription payment error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to initiate subscription. Please try again later.';
      setErrorMessage(errorMessage);
      setPaymentChoiceOpen(false);
    }
  };

  // *** NEW: Handle subscription cancellation ***
  const handleCancelSubscription = async () => {
    try {
      console.log('Canceling subscription...');
      
      const response = await axios.post('/api/subscriptions/cancel');
      setSuccessMessage(response.data.message);
      setCancelConfirmOpen(false);
      loadCurrentPlan(); // Reload plan info
      
      console.log('Subscription canceled successfully');
    } catch (error) {
      console.error('Cancel subscription error:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to cancel subscription');
      setCancelConfirmOpen(false);
    }
  };

  // *** NEW: Handle subscription reactivation ***
  const handleReactivateSubscription = async () => {
    try {
      console.log('Reactivating subscription...');
      
      const response = await axios.post('/api/subscriptions/reactivate');
      setSuccessMessage(response.data.message);
      loadCurrentPlan(); // Reload plan info
      
      console.log('Subscription reactivated successfully');
    } catch (error) {
      console.error('Reactivate subscription error:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to reactivate subscription');
    }
  };



  // Открыть диалог для поля
  const handleOpenDialog = (field) => {
    setDialogField(field);
    if (field === 'billingAll') {
      setDialogValue({
        company: user?.billingCompany || user?.company || '',
        address: user?.billingAddress || '',
        country: user?.billingCountry || '',
        city: user?.billingCity || '',
        postalCode: user?.billingPostalCode || '',
        hasVat: user?.billingHasVat ? 'yes' : 'no',
        vatNumber: user?.billingVatNumber || '',
        email: user?.billingInvoiceEmail || user?.email || ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogField(null);
    setDialogValue({
      company: '',
      address: '',
      country: '',
      city: '',
      postalCode: '',
      hasVat: 'no',
      vatNumber: '',
      email: ''
    });
  };

  // Сохранить изменения
  const handleSaveDialog = async () => {
    if (dialogField === 'billingAll') {
      const payload = {
        billingCompany: dialogValue.company,
        billingAddress: dialogValue.address,
        billingCountry: dialogValue.country,
        billingCity: dialogValue.city,
        billingPostalCode: dialogValue.postalCode,
        billingHasVat: dialogValue.hasVat === 'yes',
        billingVatNumber: dialogValue.hasVat === 'yes' ? dialogValue.vatNumber : '',
        billingInvoiceEmail: dialogValue.email
      };

      try {
        const { data: savedUser } = await axios.put('/api/users/me', payload, {
          withCredentials: true
        });
        setSuccessMessage('Updated successfully!');
        setDialogOpen(false);

        if (typeof updateUser === 'function' && savedUser) {
          updateUser(savedUser);
        }
        
        // Reset dialog values
        setDialogValue({
          company: '',
          address: '',
          country: '',
          city: '',
          postalCode: '',
          hasVat: 'no',
          vatNumber: '',
          email: ''
        });
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.error || 'Update error'
        );
      }
    }
  };

  return (
    <PageShell>
      <PageHeader title="Billing" subtitle="Plans, SMS credits, billing details and invoices" sx={{ mb: 2 }} />
      <Tabs
        value={tabIndex}
        onChange={handleChangeTab}
        variant="scrollable"
        scrollButtons="auto"
        sx={acmeTabsSx}
      >
        <Tab label="Account usage" />
        <Tab label="Email Marketing Plan" />
        <Tab label="SMS Marketing Plan" />
        <Tab label="Billing information" />
        <Tab label="Billing history" />
      </Tabs>

      <PageSnackbars {...snackbarProps} />

      {/* Account usage */}
      {tabIndex === 0 && (
        <>
          {user && !isAdminRole(user.role) && (
            <Card
              sx={{
                ...acmePageSectionCardSx,
                mt: 2,
                boxShadow: 'none',
                ...(accountStatusVariant === 'success'
                  ? acmeInfoCardActiveSx('plan')
                  : {
                      borderColor:
                        accountStatusVariant === 'error'
                          ? acme.colors.destructive
                          : acme.colors.warning,
                    }),
              }}
            >
              <CardContent sx={acmeBillingSectionContentSx}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: acme.colors.brandMuted,
                      width: 48,
                      height: 48,
                    }}
                  >
                    <StatusIcon sx={{ color: accountStatusIconColor, fontSize: '1.5rem' }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: acme.colors.foreground,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      {statusText}: {currentPlan.name}
                      {isSubscription && (
                        <Chip
                          label="Subscription"
                          size="small"
                          icon={<SubscriptionsIcon sx={{ fontSize: '1rem !important' }} />}
                        />
                      )}
                      {!isSubscription && currentPlan.name !== 'Basic' && (
                        <Chip
                          label="Admin Assigned"
                          size="small"
                          icon={<MonetizationOnIcon sx={{ fontSize: '1rem !important' }} />}
                        />
                      )}
                    </Typography>
                    {currentPlan.name === 'Basic' ? (
                      <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mt: 0.5 }}>
                        Free plan · Next upgrade: {basicPlanRenewalDateString}
                        {isBasicPlanExhausted && (
                          <Box component="span" sx={{ fontWeight: 600, color: acme.colors.warning, ml: 0.5 }}>
                            · Consider upgrading to increase your email limit
                          </Box>
                        )}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mt: 0.5 }}>
                        {isSubscription ? 'Subscription' : 'Admin assigned plan'} ·{' '}
                        {isSubscription ? 'Next billing' : 'Expires'}: {nextPaymentDateString}
                        {isPaymentDueSoon && (
                          <Box component="span" sx={{ fontWeight: 600, color: acme.colors.warning, ml: 0.5 }}>
                            · Due in {daysDifference} days
                          </Box>
                        )}
                        {isPaymentOverdue && (
                          <Box component="span" sx={{ fontWeight: 600, color: acme.colors.destructive, ml: 0.5 }}>
                            · Overdue
                          </Box>
                        )}
                        {isSubscription && subscriptionInfo?.cancel_at_period_end && (
                          <Box component="span" sx={{ fontWeight: 600, color: acme.colors.destructive, ml: 0.5 }}>
                            · Will be canceled
                          </Box>
                        )}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* *** NEW: Subscription Management Section *** */}
          {isSubscription && subscriptionInfo && !isAdminRole(user?.role) && (
            <Card sx={{ ...acmePageSectionCardSx, mb: 2, boxShadow: 'none' }}>
              <CardContent sx={acmeBillingSectionContentSx}>
              <PageBlockTitle gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SubscriptionsIcon sx={{ color: acme.colors.iconDefault, fontSize: '1.25rem' }} />
                Subscription Management
              </PageBlockTitle>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Subscription Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body1">
                    Status: 
                  </Typography>
                  <Chip 
                    label={subscriptionStatus} 
                    color={
                      subscriptionStatus === 'active' ? 'success' : 
                      subscriptionStatus === 'trialing' ? 'info' :
                      ['past_due', 'unpaid'].includes(subscriptionStatus) ? 'warning' :
                      ['canceled', 'incomplete', 'incomplete_expired'].includes(subscriptionStatus) ? 'error' :
                      'default'
                    } 
                    size="small" 
                  />
                  {subscriptionInfo.cancel_at_period_end && (
                    <Chip 
                      label="Cancelling at period end" 
                      color="error" 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                  
                  {/* *** ДОБАВЛЕНО: Предупреждения для проблемных статусов *** */}
                  {['past_due', 'unpaid'].includes(subscriptionStatus) && (
                    <Chip 
                      label="Payment Issue" 
                      color="warning" 
                      size="small" 
                      variant="outlined"
                      icon={<WarningIcon />}
                    />
                  )}
                </Box>

                {/* Subscription Dates */}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Current billing period: {subscriptionInfo.current_period_start?.toLocaleDateString()} - {subscriptionInfo.current_period_end?.toLocaleDateString()}
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  {subscriptionInfo.cancel_at_period_end ? (
                    <Button
                      variant="default"
                      startIcon={<RestoreIcon />}
                      onClick={handleReactivateSubscription}
                    >
                      Reactivate Subscription
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      startIcon={<CancelIcon />}
                      onClick={() => setCancelConfirmOpen(true)}
                      sx={{ color: acme.colors.destructive, borderColor: acme.colors.destructive }}
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </Box>

                {/* Cancellation Info */}
                <Box sx={acmeInfoCalloutSx}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <InfoIcon sx={{ color: '#666', mt: 0.5, fontSize: 18 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        About cancellation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {subscriptionInfo.cancel_at_period_end 
                          ? 'Your subscription will be canceled at the end of the current billing period. You will retain access until then and no refund will be issued.'
                          : 'Canceling will stop automatic renewals at the end of the current billing period. You will retain access until then and no refund will be issued.'
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* *** ДОБАВЛЕНО: Предупреждения для проблемных статусов *** */}
                {['past_due', 'unpaid'].includes(subscriptionStatus) && (
                  <Box sx={{ ...acmeInfoCalloutSx, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <WarningIcon sx={{ color: '#f57c00', mt: 0.5, fontSize: 18 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#f57c00' }}>
                          Payment Issue Detected:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {subscriptionStatus === 'past_due' 
                            ? 'Your payment is past due. Please update your payment method to avoid service interruption.'
                            : 'Your payment failed multiple times. Please update your payment method immediately to restore your subscription.'
                          }
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                          You still have access to your plan until {subscriptionInfo.current_period_end?.toLocaleDateString()}.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {['incomplete', 'incomplete_expired'].includes(subscriptionStatus) && (
                  <Box sx={{ p: 2, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #e57373', mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <BlockIcon sx={{ color: '#d32f2f', mt: 0.5, fontSize: 18 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#d32f2f' }}>
                          Subscription Incomplete:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Your subscription setup was not completed. Please try creating a new subscription or contact support for assistance.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
              </CardContent>
            </Card>
          )}
          
          {/* Account Status Warning */}
          {isUserRestricted() && (
            <Card sx={{ ...acmePageSectionCardSx, mb: 2, boxShadow: 'none' }}>
              <CardContent sx={acmeBillingSectionContentSx}>
              <Box sx={{ 
                p: 2, 
                ...acmeInfoCalloutSx,
                backgroundColor: user?.status === 'Block' ? acme.colors.destructive + '14' : acme.colors.muted,
                border: user?.status === 'Block' ? `1px solid ${acme.colors.destructive}` : `1px solid ${acme.colors.border}`, 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <WarningIcon sx={{ 
                    color: user?.status === 'Block' ? '#d32f2f' : '#f57c00', 
                    mt: 0.5, 
                    fontSize: 18 
                  }} />
                  <Box>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 500, 
                      color: user?.status === 'Block' ? '#d32f2f' : '#f57c00' 
                    }}>
                      {user?.status === 'Block' ? 'Account Blocked' : 'Account Under Moderation'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.status === 'Block' 
                        ? 'Your account has been blocked. All billing and subscription management features are disabled. Please contact support for assistance.'
                        : 'Your account is under moderation. Billing and subscription management features are temporarily disabled. Please wait for moderation to complete or contact support.'
                      }
                    </Typography>
                  </Box>
                </Box>
              </Box>
              </CardContent>
            </Card>
          )}
          
          <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none' }}>
            <CardContent sx={acmeBillingSectionContentSx}>
            <PageBlockTitle
              gutterBottom
              sx={{
                color: user && isAdminOrManagerRole(user.role)
                  ? acme.colors.mutedForegroundSolid
                  : acme.colors.foreground,
              }}
            >
              Your current plan:{' '}
              {user && isAdminOrManagerRole(user.role) ? 'Unlimited' : currentPlan.name}
            </PageBlockTitle>
            <Typography variant="body1" gutterBottom sx={{ color: acme.colors.mutedForegroundSolid }}>
              Emails used this month: {usedEmails} /{' '}
              {currentPlan.limit && currentPlan.limit !== Infinity
                ? currentPlan.limit
                : 'Unlimited'}
            </Typography>
            <Box sx={{ width: '100%', my: 2 }}>
              {currentPlan.limit && currentPlan.limit !== Infinity ? (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={usagePercent}
                    sx={{
                      height: 10,
                      borderRadius: acme.radius.sm,
                      bgcolor: acme.colors.muted,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor:
                          usagePercent >= 100
                            ? acme.colors.destructive
                            : usagePercent >= 90
                              ? acme.colors.warning
                              : acme.colors.brand,
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: acme.colors.mutedForegroundSolid }}>
                    {usagePercent}% of your monthly limit used
                    {usagePercent >= 90 && usagePercent < 100 && (
                      <Box component="span" sx={{ color: acme.colors.warning, fontWeight: 600, ml: 0.5 }}>
                        (approaching limit)
                      </Box>
                    )}
                    {usagePercent >= 100 && (
                      <Box component="span" sx={{ color: acme.colors.destructive, fontWeight: 600, ml: 0.5 }}>
                        (limit reached)
                      </Box>
                    )}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ mt: 1, color: acme.colors.mutedForegroundSolid }}>
                  Unlimited usage
                </Typography>
              )}
            </Box>
            </CardContent>
          </Card>

          {!isAdminRole(user?.role) && (
            <SmsCreditsUsageBar user={user} variant="inline" />
          )}
        </>
      )}
      {/* Email Marketing Plan */}
      {tabIndex === 1 && (
        <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none' }}>
          <CardContent sx={acmeBillingSectionContentSx}>
          <PageBlockTitle gutterBottom>Email Marketing Plan</PageBlockTitle>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'center',
            }}
          >
            {/* *** ПОКАЗЫВАЕМ ЗАГРУЗКУ ПЛАНОВ *** */}
            {plansLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Typography>Loading plans...</Typography>
              </Box>
            )}
            
            {/* *** ПОКАЗЫВАЕМ ОШИБКУ ЗАГРУЗКИ *** */}
            {plansError && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, color: 'warning.main' }}>
                <Typography variant="caption">
                  ⚠️ {plansError} - showing fallback prices
                </Typography>
              </Box>
            )}
            
            {/* *** ОТОБРАЖАЕМ ПЛАНЫ С НОВОЙ СИСТЕМОЙ *** */}
            {!plansLoading && availablePlans.map((plan) => {
              const isPaidPlan = plan.name !== 'Basic';
              const hasOneTimePrice = typeof plan.oneTimePrice === 'number' && plan.oneTimePrice > 0;
              const hasSubscriptionPrice = typeof plan.subscriptionPrice === 'number' && plan.subscriptionPrice > 0;
              const pricesUnavailable = isPaidPlan && !hasOneTimePrice && !hasSubscriptionPrice;

              return (
                <Card
                  key={plan.name}
                  sx={acmePlanCardSx(
                    currentPlan.name === plan.name,
                    user && isAdminOrManagerRole(user.role),
                  )}
                >
                  <CardContent
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      '&:last-child': { pb: 2 },
                    }}
                  >
                  {currentPlan.name === plan.name && (
                    <Typography variant="subtitle2" sx={acmePlanBadgeSx}>
                      Your Plan
                    </Typography>
                  )}
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Email distribution up to {plan.limit !== Infinity ? plan.limit.toLocaleString() : 'unlimited'} emails per month
                  </Typography>

                  {/* *** ЦЕНЫ: one-time + subscription *** */}
                  {plan.name === 'Basic' ? (
                    <Box sx={{ my: 1 }}>
                      <Typography variant="h5">Free</Typography>
                    </Box>
                  ) : pricesUnavailable ? (
                    <Box sx={{ my: 1 }}>
                      <Typography variant="body2" color="warning.main">
                        Price temporarily unavailable
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ my: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {hasOneTimePrice && (
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            One-time payment
                          </Typography>
                          <PriceWithTax
                            basePrice={plan.oneTimePrice}
                            user={user}
                            showBreakdown={true}
                            variant="compact"
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            for 30 days access
                          </Typography>
                        </Box>
                      )}

                      {hasSubscriptionPrice && (
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Monthly subscription
                          </Typography>
                          <PriceWithTax
                            basePrice={plan.subscriptionPrice}
                            user={user}
                            showBreakdown={true}
                            variant="compact"
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            per month, auto-renew
                          </Typography>
                          {hasOneTimePrice && plan.oneTimePrice > plan.subscriptionPrice && (
                            <Box sx={{ mt: 0.5 }}>
                              <SubscriptionSavings
                                oneTimePrice={plan.oneTimePrice}
                                subscriptionPrice={plan.subscriptionPrice}
                                variant="chip"
                                color="success"
                              />
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  <Box sx={{ mt: 'auto' }}>
                    <BrandButton
                      tone="accent"
                      variant="contained"
                      sx={{ width: '100%' }}
                      onClick={() => handleUpgradeClick(plan)}
                      disabled={
                        plan.name === 'Basic' ||
                        currentPlan.name === plan.name ||
                        (user && isAdminOrManagerRole(user.role)) ||
                        (availablePlans.findIndex(p => p.name === currentPlan.name) >
                         availablePlans.findIndex(p => p.name === plan.name)) ||
                        isUserRestricted() ||
                        pricesUnavailable
                      }
                      title={
                        plan.name === 'Basic' ? 'Free plan - no upgrade needed' :
                        pricesUnavailable ? 'Pricing is temporarily unavailable. Please refresh the page.' :
                        availablePlans.findIndex(p => p.name === currentPlan.name) >
                        availablePlans.findIndex(p => p.name === plan.name)
                          ? `You currently have the ${currentPlan.name} plan. You can switch to this plan after your current plan expires on ${nextPaymentDateString}.`
                          : "Upgrade to this plan"
                      }
                    >
                      {plan.name === 'Basic' ? 'Free Plan' : 'Upgrade'}
                    </BrandButton>
                  </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
          
          {/* *** NEW: Payment Options Info *** */}
          <Box sx={{ ...acmeInfoCalloutSx, mt: 4, position: 'relative', pt: 3 }}>
            <Box
              sx={{
                position: 'absolute',
                top: -12,
                left: 16,
                backgroundColor: acme.colors.muted,
                px: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <InfoIcon sx={{ fontSize: 18, color: acme.colors.iconDefault, mr: 0.5 }} />
              <Typography variant="caption" fontWeight="bold" sx={{ color: acme.colors.foreground }}>
                PAYMENT OPTIONS
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
              You can choose between <strong>one-time payment</strong> (manual renewal required) or <strong>monthly subscription</strong> (automatic billing). 
              Subscriptions can be canceled anytime and include prorated plan changes.
            </Typography>
          </Box>
          </CardContent>
        </Card>
      )}
      {/* SMS Marketing Plan */}
      {tabIndex === 2 && (
        <SmsBillingTab
          user={user}
          onError={setErrorMessage}
          onInfo={setSuccessMessage}
        />
      )}
      {/* Billing information */}
      {tabIndex === 3 && (
        <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none' }}>
          <CardContent sx={acmeBillingSectionContentSx}>
          <PageBlockHeader sx={{ mb: 0 }}>
            <PageBlockTitle>Billing Information</PageBlockTitle>
            <BrandButton 
              primaryHover="light"
              variant="contained" 
              onClick={() => handleOpenDialog('billingAll')}
              disabled={isUserRestricted()}
              startIcon={<EditIcon />}
            >
              Change
            </BrandButton>
          </PageBlockHeader>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', mt: 2 }}>
            <Box sx={{ display: 'grid', rowGap: '24px', width: '100%' }}>
              {/* Company */}
              <Input
                required
                label="Company"
                value={user?.billingCompany || user?.company || ''}
                variant="outlined"
                fullWidth
                disabled
                sx={acmeDisabledInputSx}
              />
              {/* Address */}
              <Input
                required
                label="Address"
                value={user?.billingAddress || ''}
                variant="outlined"
                fullWidth
                disabled
                sx={acmeDisabledInputSx}
              />
              {/* Country */}
              <Input
                required
                label="Country"
                value={user?.billingCountry || ''}
                variant="outlined"
                fullWidth
                disabled
                sx={acmeDisabledInputSx}
              />
              {/* City */}
              <Input
                required
                label="City"
                value={user?.billingCity || ''}
                variant="outlined"
                fullWidth
                disabled
                sx={acmeDisabledInputSx}
              />
              {/* Postal code */}
              <Input
                required
                label="Postal code"
                value={user?.billingPostalCode || ''}
                variant="outlined"
                fullWidth
                disabled
                sx={acmeDisabledInputSx}
              />
              {/* VAT */}
              <Input
                required
                label="VAT"
                value={user?.billingHasVat ? (user?.billingVatNumber || 'Yes') : 'No'}
                variant="outlined"
                fullWidth
                disabled
                sx={acmeDisabledInputSx}
              />
              {/* Invoice recipient Email */}
              <Input
                required
                label="Invoice recipient Email"
                value={user?.billingInvoiceEmail || user?.email || ''}
                variant="outlined"
                fullWidth
                disabled
                sx={acmeDisabledInputSx}
              />
            </Box>
          </Box>
          {/* Edit Dialog */}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
            <DialogTitleText>Edit Billing Information</DialogTitleText>
            <DialogBody>
              <Box sx={{ display: 'grid', rowGap: '16px' }}>
                <Input
                  fullWidth
                  label="Company"
                  value={dialogValue.company}
                  onChange={(e) => setDialogValue({ ...dialogValue, company: e.target.value })}
                  required
                  sx={acmeTextFieldSx}
                />
                <Input
                  fullWidth
                  label="Address"
                  value={dialogValue.address}
                  onChange={(e) => setDialogValue({ ...dialogValue, address: e.target.value })}
                  required
                  sx={acmeTextFieldSx}
                />
                <Autocomplete
                  options={countriesList}
                  value={dialogValue.country}
                  onChange={(_, newValue) => {
                    setDialogValue({
                      ...dialogValue,
                      country: newValue,
                      city: '',
                    });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Country" required sx={acmeTextFieldSx} />
                  )}
                />
                <Autocomplete
                  options={dialogValue.country ? billingAllCities
                    .filter(city => getCode(dialogValue.country) === city.country)
                    .map(city => city.name) : []}
                  value={dialogValue.city}
                  onChange={(_, newValue) => setDialogValue({ ...dialogValue, city: newValue })}
                  renderInput={(params) => (
                    <TextField {...params} label="City" required sx={acmeTextFieldSx} />
                  )}
                  disabled={!dialogValue.country}
                />
                <Input
                  fullWidth
                  label="Postal Code"
                  value={dialogValue.postalCode}
                  onChange={(e) => setDialogValue({ ...dialogValue, postalCode: e.target.value })}
                  required
                  sx={acmeTextFieldSx}
                />
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Do you have a VAT number?</FormLabel>
                  <RadioGroup
                    row
                    value={dialogValue.hasVat}
                    onChange={(_, value) => setDialogValue({ ...dialogValue, hasVat: value })}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="I have a VAT number" />
                    <FormControlLabel value="no" control={<Radio />} label="I don't have VAT number" />
                  </RadioGroup>
                  {dialogValue.hasVat === 'yes' && (
                    <Input
                      label="VAT number"
                      value={dialogValue.vatNumber}
                      onChange={(e) => setDialogValue({ ...dialogValue, vatNumber: e.target.value })}
                      required
                      fullWidth
                      sx={{ mt: 2, ...acmeTextFieldSx }}
                      helperText="Please enter VAT number in the format: [CountryCode][Number], e.g. XX000000000"
                    />
                  )}
                </FormControl>
                <Input
                  fullWidth
                  label="Invoice recipient Email"
                  value={dialogValue.email}
                  onChange={(e) => setDialogValue({ ...dialogValue, email: e.target.value })}
                  required
                  sx={acmeTextFieldSx}
                />
              </Box>
            </DialogBody>
            <DialogFooter>
              <TextCancelButton onClick={handleCloseDialog}>Cancel</TextCancelButton>
              <BrandButton onClick={handleSaveDialog}>Save</BrandButton>
            </DialogFooter>
          </Dialog>
          </CardContent>
        </Card>
      )}
      {/* Billing history */}
      {tabIndex === 4 && (
        <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none' }}>
          <CardContent sx={acmeBillingSectionContentSx}>
          <PageBlockTitle gutterBottom>Billing history</PageBlockTitle>
          <Box>
            {billingHistory.length === 0 ? (
              <EmptyState
                icon={ReceiptIcon}
                title="No billing history yet"
                description="Your payment history will appear here once you make your first purchase. Start by upgrading to a paid plan to see your invoices and transaction records."
                subtitle="All invoices can be downloaded as PDF files for your records."
              />
            ) : (
              <Table containerSx={{ boxShadow: 'none' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingHistory.map((record) => (
                    <TableRow key={record.invoiceId}>
                      <TableCell>{record.invoiceId}</TableCell>
                      <TableCell>{record.date}</TableCell>
                      <TableCell>{record.product}</TableCell>
                      <TableCell>{record.service || '-'}</TableCell>
                      <TableCell>{record.total}</TableCell>
                      <TableCell>{record.status}</TableCell>
                      <TableCell align="center">
                        <TableActionsMenuButton
                          ariaLabel="Invoice actions"
                          onClick={(event) => {
                            event.stopPropagation();
                            setInvoiceActionMenu({ anchorEl: event.currentTarget, record });
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={7}>
                      <TablePagination
                        count={billingHistory.length}
                        page={0}
                        rowsPerPage={billingHistory.length}
                        rowsPerPageOptions={[]}
                        component="div"
                        sx={{ display: 'flex', justifyContent: 'flex-end' }}
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </Box>
          </CardContent>
        </Card>
      )}

      {/* *** NEW: Payment Choice Dialog *** */}
      <Dialog open={paymentChoiceOpen} onClose={() => setPaymentChoiceOpen(false)} maxWidth="md" fullWidth>
        <DialogTitleText>
          Choose Payment Option
        </DialogTitleText>
        <DialogBody>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 2 }}>
            Select how you&apos;d like to pay for the {selectedPlan?.name} plan
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
            {/* One-time Payment Card */}
            <Card
              sx={{
                flex: 1,
                cursor: selectedPlan?.oneTimePrice > 0 ? 'pointer' : 'not-allowed',
                opacity: selectedPlan?.oneTimePrice > 0 ? 1 : 0.5,
                border: `2px solid transparent`,
                '&:hover': {
                  borderColor: selectedPlan?.oneTimePrice > 0 ? acme.colors.foreground : 'transparent',
                },
                transition: 'border-color 0.2s'
              }}
              onClick={() => {
                if (selectedPlan?.oneTimePrice > 0) handleOneTimePayment();
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <PaymentIcon sx={{ fontSize: 48, color: acme.colors.iconDefault, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  One-Time Payment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pay once for one month access
                </Typography>
                {selectedPlan?.oneTimePrice > 0 ? (
                  <PriceWithTax
                    basePrice={selectedPlan.oneTimePrice}
                    user={user}
                    variant="compact"
                    sx={{ mb: 1 }}
                  />
                ) : (
                  <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                    Unavailable
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Manual renewal required each month
                </Typography>
                <Divider sx={{ my: 2 }} />
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: acme.colors.success }} />
                    </ListItemIcon>
                    <ListItemText primary="Immediate access" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: acme.colors.success }} />
                    </ListItemIcon>
                    <ListItemText primary="No automatic charges" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: acme.colors.success }} />
                    </ListItemIcon>
                    <ListItemText primary="Full control over payments" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* Subscription Card */}
            <Card
              sx={{
                flex: 1,
                cursor: selectedPlan?.subscriptionPrice > 0 ? 'pointer' : 'not-allowed',
                opacity: selectedPlan?.subscriptionPrice > 0 ? 1 : 0.5,
                border: '2px solid transparent',
                '&:hover': {
                  borderColor: selectedPlan?.subscriptionPrice > 0 ? acme.colors.foreground : 'transparent',
                },
                transition: 'border-color 0.2s',
                position: 'relative'
              }}
              onClick={() => {
                if (selectedPlan?.subscriptionPrice > 0) handleSubscriptionPayment();
              }}
            >
              {/* *** МЕТКА ЭКОНОМИИ *** */}
              {typeof selectedPlan?.oneTimePrice === "number" &&
                typeof selectedPlan?.subscriptionPrice === "number" &&
                selectedPlan.oneTimePrice > 0 &&
                selectedPlan.subscriptionPrice > 0 &&
                selectedPlan.oneTimePrice > selectedPlan.subscriptionPrice && (
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 16, 
                    right: 16, 
                    backgroundColor: 'success.main', 
                    color: 'white', 
                    px: 1.5, 
                    py: 0.5, 
                    borderRadius: 2,
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    zIndex: 1,
                    boxShadow: 2
                  }}
                >
                  Save €{(selectedPlan.oneTimePrice - selectedPlan.subscriptionPrice).toFixed(2)}
                </Box>
              )}
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <SubscriptionsIcon sx={{ fontSize: 48, color: acme.colors.iconDefault, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Monthly Subscription
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Automatic monthly billing
                </Typography>
                {selectedPlan?.subscriptionPrice > 0 ? (
                  <PriceWithTax
                    basePrice={selectedPlan.subscriptionPrice}
                    user={user}
                    variant="compact"
                    sx={{ mb: 1 }}
                  />
                ) : (
                  <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                    Unavailable
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Per month, billed automatically
                </Typography>
                
                {/* *** ПОКАЗЫВАЕМ ЭКОНОМИЮ *** */}
                {typeof selectedPlan?.oneTimePrice === "number" &&
                  typeof selectedPlan?.subscriptionPrice === "number" &&
                  selectedPlan.oneTimePrice > selectedPlan.subscriptionPrice && (
                  <Box sx={{ mt: 1 }}>
                    <SubscriptionSavings
                      oneTimePrice={selectedPlan.oneTimePrice}
                      subscriptionPrice={selectedPlan.subscriptionPrice}
                      variant="inline"
                      color="success"
                    />
                  </Box>
                )}
                
                <Divider sx={{ my: 2 }} />
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: acme.colors.success }} />
                    </ListItemIcon>
                    <ListItemText primary="Automatic renewals" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: acme.colors.success }} />
                    </ListItemIcon>
                    <ListItemText primary="Never lose access" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: acme.colors.success }} />
                    </ListItemIcon>
                    <ListItemText primary="Easy cancellation anytime" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: acme.colors.success }} />
                    </ListItemIcon>
                    <ListItemText primary="Prorated plan changes" />
                  </ListItem>
                  {typeof selectedPlan?.oneTimePrice === "number" &&
                    typeof selectedPlan?.subscriptionPrice === "number" &&
                    selectedPlan.oneTimePrice > selectedPlan.subscriptionPrice && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <MonetizationOnIcon sx={{ fontSize: 16, color: acme.colors.success }} />
                      </ListItemIcon>
                      <ListItemText primary="Best value option" />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>
        </DialogBody>
        <DialogFooter>
          <TextCancelButton onClick={() => setPaymentChoiceOpen(false)}>
            Cancel
          </TextCancelButton>
        </DialogFooter>
      </Dialog>

      {/* *** NEW: Cancellation Confirmation Dialog *** */}
      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitleText>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: acme.colors.destructive }} />
            Cancel Subscription
          </Box>
        </DialogTitleText>
        <DialogBody>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1">
              Are you sure you want to cancel your subscription?
            </Typography>
            
            <Box sx={acmeInfoCalloutSx}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                ⚠️ Important information:
              </Typography>
              <List dense sx={{ pl: 2 }}>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <Typography variant="body2">
                    • Your subscription will remain active until {subscriptionInfo?.current_period_end?.toLocaleDateString()}
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <Typography variant="body2">
                    • You will retain full access to your plan until the end of the billing period
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <Typography variant="body2">
                    • <strong>No refund will be issued</strong> for the current billing period
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <Typography variant="body2">
                    • After cancellation, your plan will automatically change to the Basic (free) plan
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <Typography variant="body2">
                    • You can reactivate your subscription anytime before the period ends
                  </Typography>
                </ListItem>
              </List>
            </Box>
          </Box>
        </DialogBody>
        <DialogFooter>
          <TextCancelButton onClick={() => setCancelConfirmOpen(false)}>
            Keep Subscription
          </TextCancelButton>
          <ErrorContainedButton onClick={handleCancelSubscription}>
            Yes, Cancel Subscription
          </ErrorContainedButton>
        </DialogFooter>
      </Dialog>

      <TableActionsMenu
        anchorEl={invoiceActionMenu.anchorEl}
        open={Boolean(invoiceActionMenu.anchorEl)}
        onClose={() => setInvoiceActionMenu({ anchorEl: null, record: null })}
      >
        <MenuItem
          onClick={() => {
            if (invoiceActionMenu.record?.invoiceId) {
              window.open(`/api/billing/download-invoice/${invoiceActionMenu.record.invoiceId}`, '_blank');
            }
            setInvoiceActionMenu({ anchorEl: null, record: null });
          }}
        >
          Download invoice
        </MenuItem>
      </TableActionsMenu>
    </PageShell>
  );
}

export default Billing;
